import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable max-len */
import { useEffect, useRef, useState } from 'react';
import ReactPlayer from 'react-player';
import { Box, IconButton, } from '@mui/material';
import { styled } from '@mui/material/styles';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import { bgPlayer } from '../../tss/colors';
import PlayerControls from './PlayerControls';
/**
 * Styled wrapper for ReactPlayer with rounded corners
 */
const ReactPlayerStyled = styled(ReactPlayer)(({ theme }) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    borderRadius: theme.shape.borderRadius,
    '& video': {
        borderRadius: theme.shape.borderRadius, // default 5px
    },
    '& .react-player__preview': {
        borderRadius: theme.shape.borderRadius, // default 5px
    },
}));
/**
 * Initial state for video playback progress tracking
 */
const progressInitialState = {
    loaded: 0,
    loadedSeconds: 0,
    played: 0,
    playedSeconds: 0,
};
/**
 * VideoPlayer - A customizable video player component built on react-player
 *
 * Features:
 * - Custom playback controls with auto-hide functionality
 * - Fullscreen support
 * - Volume control with mute toggle
 * - Progress tracking with seek functionality
 * - Seek-ahead prevention for enforcing sequential viewing
 * - Poster image support
 * - Multiple aspect ratios
 * - Progress persistence and resume capability
 *
 * The player uses "light mode" to display a poster image before the video loads,
 * reducing unnecessary video loading. It supports programmatic seeking via the
 * seekPosition prop, and tracks the maximum watched position to prevent skipping
 * ahead when disableSeekAhead is enabled.
 *
 * @example
 * ```tsx
 * <VideoPlayer
 *   url="https://example.com/video.mp4"
 *   posterUrl="https://example.com/poster.jpg"
 *   aspectRatio="16:9"
 *   showControls={true}
 *   disableSeekAhead={true}
 *   initialMaxPosition={45}
 *   onProgress={(current, max) => console.log('Progress:', current)}
 *   onEnd={() => console.log('Video ended')}
 * />
 * ```
 */
const VideoPlayer = ({ url, posterUrl, playerStyle, onProgress, showControls, seekPosition, initialMaxPosition, seekTrigger, onEnd, onError, onPause, onDuration, aspectRatio, handleNextPrevious = VideoPlayer.defaultProps.handleNextPrevious, disableSeekAhead = false, }) => {
    const playerRef = useRef(null);
    const fullScreen = useFullScreenHandle();
    const [lightMode, setLightMode] = useState(true);
    const [playing, setPlaying] = useState(false);
    const [duration, setDuration] = useState(0);
    const [progress, setProgress] = useState(progressInitialState);
    const [volume, setVolume] = useState(50);
    const [muted, setMuted] = useState(false);
    const [ready, setReady] = useState(false);
    const [seekOnReady, setSeekOnReady] = useState();
    const [maxPosition, setMaxPosition] = useState(initialMaxPosition || 0);
    const [seeking, setSeeking] = useState(false);
    const [controlsVisible, setControlsVisible] = useState(true);
    const controlsTimeoutRef = useRef(null);
    const videoBoxRef = useRef(null);
    useEffect(() => {
        setReady(false);
        setMaxPosition(0);
    }, [url]);
    useEffect(() => {
        if (seekPosition !== undefined && seekPosition !== null && seekPosition >= 0) {
            // console.warn('effect Seeking to:', seekPosition);
            setProgress({
                loaded: 0,
                loadedSeconds: 0,
                played: seekPosition / duration,
                playedSeconds: seekPosition,
            });
            if (seekPosition > 0) {
                setLightMode(false);
            }
            if (ready) {
                // setPlaying(true);
                playerRef?.current?.seekTo(seekPosition);
            }
            else {
                // setPlaying(true);
                setSeekOnReady(seekPosition);
            }
        }
    }, [
        duration,
        ready,
        seekPosition,
        seekTrigger,
        url,
    ]);
    const handleSeekStart = (value) => {
        const seekTo = disableSeekAhead ? Math.min(value, maxPosition) : value;
        const currentPosition = playerRef?.current?.getCurrentTime();
        if (Math.abs(seekTo - currentPosition) < 1) {
            console.warn('ignoring seek to same position');
            return;
        }
        console.warn('handleSeekStart', {
            value,
            maxPosition,
        });
        setSeeking(true);
        setProgress({ ...progress, playedSeconds: seekTo });
        setLightMode(false);
        playerRef?.current?.seekTo(seekTo);
    };
    const handleOnReady = () => {
        setReady(true);
        if (seekOnReady) {
            handleSeekStart(seekOnReady);
            setSeekOnReady(null);
        }
    };
    const handleClickFullscreen = (state, isButton) => {
        if (isButton) {
            if (state) {
                fullScreen.enter();
            }
            else {
                fullScreen.exit();
            }
        }
    };
    const handleDuration = (value) => {
        if (onDuration)
            onDuration(value);
        setDuration(value);
    };
    const handleProgress = (state) => {
        if (!playerRef?.current?.getInternalPlayer()?.seeking) {
            const currentPosition = playerRef?.current?.getCurrentTime();
            setProgress(state);
            const newMaxPosition = Math.max(maxPosition, currentPosition);
            if (newMaxPosition > maxPosition) {
                setMaxPosition(newMaxPosition);
            }
            if (onProgress) {
                onProgress(currentPosition, newMaxPosition);
            }
        }
    };
    const handleSeek = () => {
        console.log('handleSeek', {
            playerCurrentTime: playerRef?.current?.getCurrentTime(),
            played: maxPosition,
            progress,
        });
        if (seeking && disableSeekAhead && playerRef?.current?.getCurrentTime() > maxPosition) {
            console.warn(' resetting to', maxPosition);
            playerRef?.current?.seekTo(maxPosition);
            setSeeking(false);
        }
    };
    // const handleSeekCommit = () => {
    //   console.log('seek commit');
    //   // setSeeking(false);
    // };
    const handleEndVideo = () => {
        onEnd();
    };
    const getAspectRatioValue = () => {
        switch (aspectRatio) {
            case '1:1': return '100%';
            case '16:9': return '56.25%';
            case '4:3': return '75%';
            case '3:2': return '66.66%';
            case '8:5': return '62.5%';
            default: {
                console.error('unknown aspect ratio!');
                return '0';
            }
        }
    };
    const resetControlsTimer = () => {
        clearTimeout(controlsTimeoutRef.current);
        setControlsVisible(true);
        controlsTimeoutRef.current = setTimeout(() => {
            setControlsVisible(false);
        }, 2000); // hide after 2 seconds of inactivity
    };
    const handleMouseLeave = () => {
        clearTimeout(controlsTimeoutRef.current);
        setControlsVisible(false);
    };
    const handleError = (error) => {
        console.error('VideoPlayer error:', error);
        if (onError) {
            onError(error);
        }
    };
    useEffect(() => {
        const handleMouseEnter = () => {
            setControlsVisible(true);
            resetControlsTimer();
        };
        const handleUserActivity = () => {
            resetControlsTimer();
        };
        if (showControls) {
            const videoBox = videoBoxRef.current;
            if (videoBox) {
                videoBox.addEventListener('mouseenter', handleMouseEnter);
                videoBox.addEventListener('mouseleave', handleMouseLeave);
                videoBox.addEventListener('mousemove', handleUserActivity);
                videoBox.addEventListener('click', handleUserActivity);
                videoBox.addEventListener('touchstart', handleUserActivity, { passive: true });
            }
            resetControlsTimer();
            return () => {
                clearTimeout(controlsTimeoutRef.current);
                if (videoBox) {
                    videoBox.removeEventListener('mouseenter', handleMouseEnter);
                    videoBox.removeEventListener('mouseleave', handleMouseLeave);
                    videoBox.removeEventListener('mousemove', handleUserActivity);
                    videoBox.removeEventListener('click', handleUserActivity);
                    videoBox.removeEventListener('touchstart', handleUserActivity);
                }
            };
        }
        return undefined;
    }, [
        showControls,
        videoBoxRef,
    ]);
    // eslint-disable-next-line no-nested-ternary
    const lightProp = lightMode ? (posterUrl || true) : false;
    // console.log('video', {
    //   // url,
    //   // posterUrl,
    //   // playing,
    //   seekPosition,
    //   maxPosition,
    //   // lightMode,
    // });
    return (_jsx(Box, { ref: videoBoxRef, sx: {
            position: 'relative',
            paddingTop: getAspectRatioValue(), /* default 16:9 Aspect Ratio (divide 9 by 16 = 0.5625) */
            borderRadius: 1,
        }, children: _jsxs(FullScreen, { handle: fullScreen, onChange: (state) => handleClickFullscreen(state, false), children: [_jsx(ReactPlayerStyled, { ref: playerRef, url: url, volume: muted ? 0 : (volume / 100), muted: muted, playing: playing, controls: false, 
                    // seeking not possible on light mode until user click on play button
                    // * TODO: add thumbnail image to avoid unnecessary video load (*** Not for NoteView)
                    light: lightProp, width: "100%", height: "100%", 
                    // onPlay={setPlaying(true)}
                    onProgress: handleProgress, onDuration: handleDuration, onEnded: handleEndVideo, onReady: handleOnReady, onPause: onPause, onSeek: handleSeek, onError: handleError, 
                    // onBufer={() => console.log('buffering')}
                    // onBufferEnd={() => console.log('buffer end')}
                    playIcon: (_jsx(IconButton, { onClick: () => setPlaying(true), children: _jsx(PlayCircleOutlineIcon, { sx: {
                                width: '69.5px',
                                height: '69.5px',
                                color: '#f0f0f0',
                            } }) })), style: { backgroundColor: bgPlayer }, sx: {
                        '& .react-player__preview': {
                            ...playerStyle,
                        },
                        '& video': {
                            borderRadius: 1, // default 5px
                            ...playerStyle,
                        },
                    } }), showControls && controlsVisible && (_jsx(PlayerControls, { playing: playing, muted: muted, volume: volume, progress: progress, isFullScreen: fullScreen.active, duration: duration, setPlaying: (state) => {
                        if (lightMode) {
                            setLightMode(false);
                        }
                        setPlaying(state);
                    }, setVolume: setVolume, setMuted: setMuted, handleNextPrevious: handleNextPrevious, handleSeekStart: handleSeekStart, 
                    // handleSeekCommit={handleSeekCommit}
                    handleClickFullscreen: handleClickFullscreen, disableSkipNext: disableSeekAhead, maxAllowedValue: disableSeekAhead ? maxPosition : duration }))] }) }));
};
VideoPlayer.defaultProps = {
    disableSeekAhead: false,
    showControls: true,
    playerStyle: {},
    aspectRatio: '16:9',
    posterUrl: undefined,
    seekPosition: undefined,
    seekTrigger: undefined,
    initialMaxPosition: undefined,
    onError: (error) => {
        console.error('VideoPlayer error:', error);
    },
    onPause: () => { },
    onProgress: () => { },
    onDuration: () => { },
    handleNextPrevious: () => { },
};
export default VideoPlayer;
//# sourceMappingURL=VideoPlayer.js.map